/**
 * "SKJV It is Written Report" modal. Shows the structured markdown
 * report in an editable textarea with three actions: Copy, Report
 * (submit to feedback inbox), Close.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon, CheckIcon, CopyIcon, WarningIcon } from '@components/icons';

interface CommentaryReportDialogProps {
  verseRef: string;
  initialMarkdown: string;
  onSubmit: (markdown: string) => Promise<void> | void;
  onClose: () => void;
}

export function CommentaryReportDialog({
  verseRef,
  initialMarkdown,
  onSubmit,
  onClose,
}: CommentaryReportDialogProps) {
  const [markdown, setMarkdown] = useState(initialMarkdown);
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Copy failed');
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(markdown);
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Report failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative flex w-full max-w-2xl flex-col rounded-2xl bg-white p-6 shadow-xl dark:bg-stone-800">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-stone-800 dark:text-stone-200">
            SKJV It is Written Report — {verseRef}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 text-stone-400 transition-colors hover:text-stone-600 dark:hover:text-stone-300"
            aria-label="Close"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>

        <textarea
          ref={textareaRef}
          value={markdown}
          onChange={(e) => setMarkdown(e.target.value)}
          rows={18}
          spellCheck={false}
          className="w-full flex-1 resize-y rounded-lg border border-stone-300 bg-stone-50 px-3 py-2 font-mono text-xs leading-relaxed text-stone-800 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 dark:border-stone-600 dark:bg-stone-900 dark:text-stone-200"
        />

        {error && (
          <p className="mt-3 text-xs text-red-500 dark:text-red-400">{error}</p>
        )}

        <div className="mt-4 flex gap-3">
          <button
            onClick={onClose}
            disabled={submitting}
            className="flex-1 rounded-lg border border-stone-300 px-4 py-2 text-stone-600 transition-colors hover:bg-stone-100 disabled:opacity-50 dark:border-stone-600 dark:text-stone-400 dark:hover:bg-stone-700"
          >
            Close
          </button>
          <button
            onClick={handleCopy}
            disabled={submitting}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-stone-300 px-4 py-2 text-stone-700 transition-colors hover:bg-stone-100 disabled:opacity-50 dark:border-stone-600 dark:text-stone-300 dark:hover:bg-stone-700"
          >
            {copied ? (
              <CheckIcon className="h-4 w-4 text-emerald-500" />
            ) : (
              <CopyIcon className="h-4 w-4" />
            )}
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || markdown.trim().length === 0}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-white transition-colors hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitted ? (
              <CheckIcon className="h-4 w-4" />
            ) : (
              <WarningIcon className="h-4 w-4" />
            )}
            {submitting ? 'Reporting…' : submitted ? 'Reported' : 'Report'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
